import type {
  AnchorHTMLAttributes,
  MouseEventHandler,
  MouseEvent,
  ReactNode,
} from 'react';
import { useCallback, forwardRef } from 'react';

import history from '@/history';
import { getAccountContextForHistory } from '@routes/utils';
import { useAppSelector } from '@store/hooks';
import { selectAccountContextFromAccount } from '@store/reducers/reducer-auth';

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  children: ReactNode;
  className?: string;
  useContext?: boolean;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}

function isLeftClickEvent(event: MouseEvent<HTMLAnchorElement>) {
  return event.button === 0;
}

function isModifiedEvent(event: MouseEvent<HTMLAnchorElement>) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    { useContext = true, to, children, onClick, disabled = false, ...props },
    forwardedRef,
  ) => {
    const accountContext = useAppSelector(selectAccountContextFromAccount);

    const context = useContext
      ? getAccountContextForHistory(accountContext)
      : '';

    const handleClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        if (onClick) {
          onClick(event);
        }

        if (isModifiedEvent(event) || !isLeftClickEvent(event)) {
          return;
        }

        if (event.defaultPrevented === true) {
          return;
        }

        event.preventDefault();
        history?.push(`${context}${to}`);
      },
      [onClick, to, context, disabled],
    );

    return (
      <a
        ref={forwardedRef}
        href={`${context}${to}`}
        {...props}
        onClick={handleClick}
      >
        {children}
      </a>
    );
  },
);
Link.displayName = 'Link';

export default Link;
